import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismTable,
  PrismTableCellsTdTitleAttribute,
} from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import MarketTotalRevenueCellsTdChangeWithRelative from 'lumen/components/market/total-revenue/cells/td-change-with-relative';
import MarketTotalRevenueCellsTdFilter from 'lumen/components/market/total-revenue/cells/td-filter';
import MarketTotalRevenueCellsThTitleAttribute from 'lumen/components/market/total-revenue/cells/th-title-attribute';
import type { TotalRevenueItem } from 'lumen/models';
import type Item from 'lumen/utils/datamodels/total-revenue/item';

const COLUMN_PROPERTIES = {
  ID: 'id',
  NAME: 'name',
  FILTER: 'filter',
  TOTAL_REVENUE: 'totalRevenue',
  ROOM_REVENUE: 'roomRevenue',
  NON_ROOM_REVENUE: 'nonRoomRevenue',
  NON_ROOM_PREMIUM: 'nonRoomPremium',
};

interface ItemWithDisplayName extends Item {
  displayName?: string;
}

interface MarketTotalRevenueTablePortfolioSignature {
  Args: {
    focusedProperties: string[];
    isLoading: boolean;
    isPerAvailableRoom: boolean;
    onPropertyClick: (propertyId: string) => void;
    portfolio: TotalRevenueItem[] | undefined;
  };
}

export default class MarketTotalRevenueTablePortfolio extends Component<MarketTotalRevenueTablePortfolioSignature> {
  @service declare intl: Services['intl'];
  @service('total-revenue/summation')
  declare totalRevenueSummation: Services['total-revenue/summation'];

  initialSorts = [
    {
      valuePath: COLUMN_PROPERTIES.NAME,
      isAscending: true,
    },
  ];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const referenceValues =
      this.args.portfolio?.[0]?.sortedReferenceValues ?? [];

    const columns: CustomEmberTableColumn[] = [
      {
        name: this.intl.t('total_revenue.portfolio.name'),
        valuePath: COLUMN_PROPERTIES.NAME,
        tdComponent: PrismTableCellsTdTitleAttribute,
        textAlign: 'left' as const,
        width: 250,
        isFixed: 'left',
      },
      {
        name: '',
        valuePath: COLUMN_PROPERTIES.FILTER,
        tdComponent: MarketTotalRevenueCellsTdFilter,
        isSortable: false,
        minWidth: 48,
        width: 48,
        maxWidth: 48,
        isFixed: 'left',
        hasRightBorder: true,
      },
    ];

    columns.push({
      name: this.args.isPerAvailableRoom
        ? this.intl.t('general.trevpar')
        : this.intl.t('total_revenue.total_revenue'),
      valuePath: COLUMN_PROPERTIES.TOTAL_REVENUE,
      displayValuePath: `displayValue.${COLUMN_PROPERTIES.TOTAL_REVENUE}`,
      thComponent: MarketTotalRevenueCellsThTitleAttribute,
      thSortWrapperClassNames: 'truncate',
      textAlign: 'right' as const,
      width: 200,
    });

    referenceValues.forEach((referenceValue, index) => {
      columns.push({
        name: this.intl.t('general.versus.option', {
          option: referenceValue.translatedReferenceType,
        }),
        valuePath: `${referenceValue.referenceType}_${COLUMN_PROPERTIES.TOTAL_REVENUE}`,
        displayValuePath: `displayValue.${referenceValue.referenceType}_${COLUMN_PROPERTIES.TOTAL_REVENUE}`,
        thComponent: MarketTotalRevenueCellsThTitleAttribute,
        thSortWrapperClassNames: 'truncate',
        textAlign: 'right' as const,
        tdComponent: MarketTotalRevenueCellsTdChangeWithRelative,
        width: 200,
        hasRightBorder: index === referenceValues.length - 1,
      });
    });

    columns.push({
      name: this.args.isPerAvailableRoom
        ? this.intl.t('general.revpar')
        : this.intl.t('total_revenue.room_revenue'),
      textAlign: 'right' as const,
      valuePath: COLUMN_PROPERTIES.ROOM_REVENUE,
      displayValuePath: `displayValue.${COLUMN_PROPERTIES.ROOM_REVENUE}`,
      thComponent: MarketTotalRevenueCellsThTitleAttribute,
      thSortWrapperClassNames: 'truncate',
      width: 200,
    });

    referenceValues.forEach((referenceValue, index) => {
      columns.push({
        name: this.intl.t('general.versus.option', {
          option: referenceValue.translatedReferenceType,
        }),
        valuePath: `${referenceValue.referenceType}_${COLUMN_PROPERTIES.ROOM_REVENUE}`,
        displayValuePath: `displayValue.${referenceValue.referenceType}_${COLUMN_PROPERTIES.ROOM_REVENUE}`,
        thComponent: MarketTotalRevenueCellsThTitleAttribute,
        thSortWrapperClassNames: 'truncate',
        textAlign: 'right' as const,
        tdComponent: MarketTotalRevenueCellsTdChangeWithRelative,
        width: 200,
        hasRightBorder: index === referenceValues.length - 1,
      });
    });

    columns.push({
      name: this.args.isPerAvailableRoom
        ? this.intl.t('general.arevpar')
        : this.intl.t('total_revenue.non_room_revenue'),
      textAlign: 'right' as const,
      valuePath: COLUMN_PROPERTIES.NON_ROOM_REVENUE,
      displayValuePath: `displayValue.${COLUMN_PROPERTIES.NON_ROOM_REVENUE}`,
      thComponent: MarketTotalRevenueCellsThTitleAttribute,
      thSortWrapperClassNames: 'truncate',
      width: 200,
    });

    referenceValues.forEach((referenceValue, index) => {
      columns.push({
        name: this.intl.t('general.versus.option', {
          option: referenceValue.translatedReferenceType,
        }),
        valuePath: `${referenceValue.referenceType}_${COLUMN_PROPERTIES.NON_ROOM_REVENUE}`,
        displayValuePath: `displayValue.${referenceValue.referenceType}_${COLUMN_PROPERTIES.NON_ROOM_REVENUE}`,
        thComponent: MarketTotalRevenueCellsThTitleAttribute,
        thSortWrapperClassNames: 'truncate',
        textAlign: 'right' as const,
        tdComponent: MarketTotalRevenueCellsTdChangeWithRelative,
        width: 200,
        hasRightBorder: index === referenceValues.length - 1,
      });
    });

    columns.push({
      name: this.intl.t('total_revenue.non_room_premium'),
      textAlign: 'right' as const,
      valuePath: COLUMN_PROPERTIES.NON_ROOM_PREMIUM,
      displayValuePath: `displayValue.${COLUMN_PROPERTIES.NON_ROOM_PREMIUM}`,
      thComponent: MarketTotalRevenueCellsThTitleAttribute,
      thSortWrapperClassNames: 'truncate',
      width: 200,
    });

    referenceValues.forEach((referenceValue, index) => {
      columns.push({
        name: this.intl.t('general.versus.option', {
          option: referenceValue.translatedReferenceType,
        }),
        valuePath: `${referenceValue.referenceType}_${COLUMN_PROPERTIES.NON_ROOM_PREMIUM}`,
        displayValuePath: `displayValue.${referenceValue.referenceType}_${COLUMN_PROPERTIES.NON_ROOM_PREMIUM}`,
        thComponent: MarketTotalRevenueCellsThTitleAttribute,
        thSortWrapperClassNames: 'truncate',
        textAlign: 'right' as const,
        tdComponent: MarketTotalRevenueCellsTdChangeWithRelative,
        width: 200,
        hasRightBorder: index === referenceValues.length - 1,
      });
    });

    return columns;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get footerRows() {
    if (!this.args.portfolio) {
      return [];
    }

    const totalRowItem: ItemWithDisplayName =
      this.totalRevenueSummation.getSummatedItem(this.args.portfolio);
    totalRowItem.displayName = this.intl.t('total_revenue.total');

    const totalRow = this.getRowDataForItem(totalRowItem, { isTotal: true });

    return [totalRow];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    if (!this.args.portfolio) {
      return [];
    }

    return this.args.portfolio.map((portfolioItem) =>
      this.getRowDataForItem(portfolioItem),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getRowDataForItem(
    item: TotalRevenueItem | ItemWithDisplayName,
    { isTotal = false } = {},
  ) {
    const propertyId =
      'subscription' in item ? item.subscription?.id : undefined;

    const isFilterable = !isTotal;
    const isSelected = this.args.focusedProperties.includes(
      propertyId as string,
    );

    let className = 'table-row';
    if (isFilterable) {
      className = `${className} table-row--filterable`;
    }
    if (isSelected) {
      className = `${className} table-row--selected`;
    }
    if (isTotal) {
      className = `${className} table-row--total`;
    }

    const rowData: CustomEmberTableRow & { displayValue: CustomEmberTableRow } =
      {
        className,

        [COLUMN_PROPERTIES.ID]: propertyId,
        [COLUMN_PROPERTIES.NAME]: item.displayName,
        [COLUMN_PROPERTIES.TOTAL_REVENUE]: item.totalRevenueOrPAR,
        [COLUMN_PROPERTIES.ROOM_REVENUE]: item.totalRoomRevenueOrPAR,
        [COLUMN_PROPERTIES.NON_ROOM_REVENUE]: item.totalNonRoomRevenueOrPAR,
        [COLUMN_PROPERTIES.NON_ROOM_PREMIUM]: item.nonRoomPremium,
        [COLUMN_PROPERTIES.FILTER]: {
          ariaLabel: isSelected
            ? this.intl.t('total_revenue.remove_from_page_filter', {
                filter: item.displayName,
              })
            : this.intl.t('total_revenue.add_to_page_filter', {
                filter: item.displayName,
              }),
          handleFilter: (event: Event) => {
            event.stopPropagation();
            this.args.onPropertyClick(propertyId as string);
          },
        },

        displayValue: {
          [COLUMN_PROPERTIES.TOTAL_REVENUE]: item.formattedTotalRevenue,
          [COLUMN_PROPERTIES.ROOM_REVENUE]: item.formattedTotalRoomRevenue,
          [COLUMN_PROPERTIES.NON_ROOM_REVENUE]:
            item.formattedTotalNonRoomRevenue,
          [COLUMN_PROPERTIES.NON_ROOM_PREMIUM]: item.formattedNonRoomPremium,
        },
      };

    item.referenceValues.forEach((referenceValue) => {
      const totalRevenueKey = `${referenceValue.referenceType}_${COLUMN_PROPERTIES.TOTAL_REVENUE}`;
      const roomRevenueKey = `${referenceValue.referenceType}_${COLUMN_PROPERTIES.ROOM_REVENUE}`;
      const nonRoomRevenueKey = `${referenceValue.referenceType}_${COLUMN_PROPERTIES.NON_ROOM_REVENUE}`;
      const nonRoomPremiumKey = `${referenceValue.referenceType}_${COLUMN_PROPERTIES.NON_ROOM_PREMIUM}`;

      rowData[totalRevenueKey] = referenceValue.totalRevenueRelativeChange;
      rowData[roomRevenueKey] = referenceValue.totalRoomRevenueRelativeChange;
      rowData[nonRoomRevenueKey] =
        referenceValue.totalNonRoomRevenueRelativeChange;
      rowData[nonRoomPremiumKey] = referenceValue.nonRoomPremiumRelativeChange;

      rowData.displayValue[totalRevenueKey] = {
        change: referenceValue.formattedTotalRevenueChange,
        relativeChange: referenceValue.formattedTotalRevenueRelativeChange,
      };
      rowData.displayValue[roomRevenueKey] = {
        change: referenceValue.formattedTotalRoomRevenueChange,
        relativeChange: referenceValue.formattedTotalRoomRevenueRelativeChange,
      };
      rowData.displayValue[nonRoomRevenueKey] = {
        change: referenceValue.formattedTotalNonRoomRevenueChange,
        relativeChange:
          referenceValue.formattedTotalNonRoomRevenueRelativeChange,
      };
      rowData.displayValue[nonRoomPremiumKey] = {
        change: referenceValue.formattedNonRoomPremiumChange,
        relativeChange: referenceValue.formattedNonRoomPremiumRelativeChange,
      };
    });

    return rowData;
  }

  @action handleRowClick(row: { rowValue: CustomEmberTableRow }): void {
    if (!row.rowValue[COLUMN_PROPERTIES.ID]) {
      return;
    }

    this.args.onPropertyClick(row.rowValue[COLUMN_PROPERTIES.ID] as string);
  }

  <template>
    <div>
      <PrismTable
        @columns={{this.columns}}
        @enableAutoSortsUpdate={{true}}
        @footerRows={{this.footerRows}}
        @initialSorts={{this.initialSorts}}
        @isLoading={{@isLoading}}
        @nrOfEmptyRows={{2}}
        @nrOfLoadingRows={{8}}
        @onRowClick={{this.handleRowClick}}
        @rows={{this.rows}}
        @widthConstraint="gte-container-slack"
        class="table-portfolio"
        data-test-id="table-portfolio"
      />
    </div>
  </template>
}
